import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { API } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { Button } from '../components/ui/button';
import { competenceLabel } from '../lib/format';
import { FileDown, FileText, Loader2, Building2, Calendar, Info } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Relatórios — Download de Relatório Executivo Mensal em PDF.
 *
 * O escopo (empresa OU consolidado) e a competência vêm do contexto global
 * (TopBar). Basta clicar em "Baixar Relatório PDF" para receber o arquivo.
 */
export default function Relatorios() {
  const { competence, companyFilter, companies } = useApp();
  const [busy, setBusy] = useState(false);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === companyFilter) || null,
    [companies, companyFilter],
  );
  const isConsolidado = companyFilter === 'all';
  const scopeLabel = isConsolidado ? 'Grupo Consolidado' : selectedCompany?.name || 'Empresa';

  const download = async () => {
    setBusy(true);
    try {
      const params = { competence };
      if (!isConsolidado) params.company_id = companyFilter;
      const res = await axios.get(`${API}/reports/monthly-pdf`, {
        params,
        responseType: 'blob',
        withCredentials: true,
        timeout: 120000,
      });
      // Nome do arquivo: pegar do header ou compor
      const disp = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match ? match[1] : `relatorio-executivo_${competence}.pdf`;

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório baixado com sucesso');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erro ao gerar relatório';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-in">
      <TopBar
        title="Relatórios"
        subtitle="Baixe o Relatório Executivo Mensal em PDF — respeitando o escopo e a competência escolhidos no topo"
      />
      <div className="px-8 py-7 space-y-5 max-w-4xl">
        <SectionCard
          title="Relatório Executivo Mensal"
          subtitle="PDF profissional com KPIs, resultado por empresa, evolução dos últimos 6 meses e todos os lançamentos do mês"
          testid="report-monthly"
        >
          <div className="p-6 space-y-6">
            {/* Cartão de escopo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 px-4 py-3 bg-zinc-900/40 border border-zinc-800 rounded-md">
                <Building2 size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <div className="overline text-zinc-500">Escopo</div>
                  <div className="text-zinc-100 font-medium mt-1" data-testid="report-scope-label">
                    {scopeLabel}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {isConsolidado
                      ? 'Consolidado de todas as empresas visíveis para você'
                      : 'Somente esta empresa — troque no seletor do topo se quiser outro escopo'}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 px-4 py-3 bg-zinc-900/40 border border-zinc-800 rounded-md">
                <Calendar size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <div className="overline text-zinc-500">Competência</div>
                  <div className="text-zinc-100 font-medium mt-1" data-testid="report-competence-label">
                    {competenceLabel(competence)}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Troque a competência no seletor do topo para gerar outro mês
                  </div>
                </div>
              </div>
            </div>

            {/* O que vai vir dentro */}
            <div className="border border-zinc-800 rounded-md">
              <div className="px-4 py-3 border-b border-zinc-900">
                <div className="text-[13px] font-medium text-zinc-100 flex items-center gap-2">
                  <FileText size={14} className="text-[#D4AF37]" />
                  Conteúdo do PDF
                </div>
              </div>
              <ul className="p-4 space-y-2 text-[12.5px] text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">•</span>
                  <span>Cabeçalho com data e hora de geração (fuso Brasília — UTC-3)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">•</span>
                  <span>KPIs do mês: Receita, Custos, Despesas, Impostos, Investimentos, <strong>Lucro Líquido</strong>, Margem e EBITDA</span>
                </li>
                {isConsolidado && (
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF37] mt-0.5">•</span>
                    <span>Tabela de resultado por empresa (com participação societária)</span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">•</span>
                  <span>Gráfico de evolução dos últimos 6 meses (Receita, Custos, Despesas, Lucro Líquido)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">•</span>
                  <span>Tabela de valores mensais dos últimos 6 meses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">•</span>
                  <span><strong>Todas</strong> as receitas do mês (linha a linha) com total</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">•</span>
                  <span><strong>Todas</strong> as saídas do mês (Custos, Despesas, Impostos, Investimentos) com total</span>
                </li>
              </ul>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
              <div className="flex items-start gap-2 text-[11.5px] text-zinc-500 max-w-lg">
                <Info size={13} className="text-zinc-600 mt-0.5 shrink-0" />
                <span>
                  A geração pode levar alguns segundos dependendo da quantidade de lançamentos.
                  O arquivo abre no seu navegador ou vai direto para a pasta de Downloads.
                </span>
              </div>
              <Button
                onClick={download}
                disabled={busy}
                className="btn-gold gap-2 px-6 py-5 text-[13px]"
                data-testid="btn-download-monthly-report"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                {busy ? 'Gerando PDF...' : 'Baixar Relatório PDF'}
              </Button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
